import { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';

export interface AuthGuardProps {
  /** Whether auth state is still resolving. */
  isLoading: boolean;
  /** Whether the user is authenticated (used only for the popup-close effect). */
  isAuthenticated: boolean;
  /** App content (the router). Route-level beforeLoad guards do the redirecting. */
  children: React.ReactNode;
  /**
   * Branded element shown while auth resolves. Defaults to a bare spinner.
   * Apps pass a neutral splash (logo) — NOT a login skeleton, which would flash
   * login → app for already-authenticated users.
   */
  fallback?: React.ReactNode;
}

/**
 * Boot gate: blocks rendering the router until auth state has resolved, so a
 * protected route's beforeLoad doesn't bounce an authenticated user to /login
 * before /auth/me returns. Also closes OAuth popups once authenticated.
 */
export function AuthGuard({ isLoading, isAuthenticated, children, fallback }: AuthGuardProps) {
  useEffect(() => {
    const isPopup = new URLSearchParams(window.location.search).get('popup') === 'true';
    if (isAuthenticated && isPopup && window.opener) {
      window.close();
    }
  }, [isAuthenticated]);

  // Keep the splash painted over the app and fade it OUT once auth resolves, so
  // the loading → login (or → app) hand-off is a crossfade rather than an
  // instant flicker. Children still only mount after auth resolves, so a
  // protected route's beforeLoad never bounces an authenticated user.
  const [splashMounted, setSplashMounted] = useState(isLoading);
  useEffect(() => {
    if (isLoading) setSplashMounted(true);
  }, [isLoading]);

  const splash = fallback ?? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#121212',
      }}
    >
      <CircularProgress />
    </Box>
  );

  return (
    <>
      {!isLoading && children}
      {splashMounted && (
        <Box
          aria-hidden={!isLoading}
          onTransitionEnd={(e) => {
            if (e.propertyName === 'opacity' && !isLoading) setSplashMounted(false);
          }}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            opacity: isLoading ? 1 : 0,
            transition: 'opacity 280ms cubic-bezier(0.23, 1, 0.32, 1)',
            pointerEvents: isLoading ? 'auto' : 'none',
          }}
        >
          {splash}
        </Box>
      )}
    </>
  );
}
